import * as React from 'react';
import { addYears } from 'date-fns';
import { useMutation } from 'react-query';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import type { DeliveryVoucherFilters as Filters } from 'hooks/store/delivery-vouchers';
import { useDeliveryVouchersSearchParams } from 'hooks/store/delivery-vouchers';
import {
  useCarrierOptions,
  useCityOptions,
  useCompanyOptions,
  useSupplierOptions,
  useUfOptions,
} from 'hooks/api/options';
import { Form, FormField } from 'components/ui/form';
import {
  ComboboxGs,
  DatePickerGs,
  DateRangePickerGs,
  InputGs,
  LabelAndCheckbox,
  MultiSelectGs,
  type SelectContentProps,
} from 'components/ui/Forms';
import { Button } from 'components/ui/ButtonGs';

import { formSchema, type FormSchema } from './form-schema';
import { createFilters } from './utils';

type Ref = {
  resetFilters: () => void;
};

type Props = {
  onSubmitFilters: (filters: Filters) => void;
  onResetFilters: () => void;
};

export type DeliveryVoucherFiltersRef = Ref;

const EMPTY_FORM_VALUES: FormSchema = {
  supplierId: undefined,
  companyId: undefined,
  invoiceNumber: '',
  emissionDate: undefined,
  deliveryDate: undefined,
  carrierId: undefined,
  sameCarrierCnpj: false,
  originUf: undefined,
  originIbge: undefined,
  destinationUf: undefined,
  destinationIbge: undefined,
};

export const DeliveryVoucherFilters = React.forwardRef<Ref, Props>(
  ({ onSubmitFilters, onResetFilters }, ref) => {
    const { filters } = useDeliveryVouchersSearchParams();

    // Opções usadas pelos campos de seleção do formulário.
    const ufOptions = useUfOptions();
    const carrierOptions = useCarrierOptions({
      labelType: 'trade-name-and-document',
    });
    const supplierOptions = useSupplierOptions({
      labelType: 'trade-name-and-document',
    });
    const companyOptions = useCompanyOptions({ labelType: 'trade-name' });
    const { fetchCities } = useCityOptions();

    // Estado local apenas para opções dependentes de UF e controle da hidratação inicial.
    const [formHydrated, setFormHydrated] = React.useState(false);
    const [originCityOptions, setOriginCityOptions] = React.useState<
      SelectContentProps[]
    >([]);
    const [destinationCityOptions, setDestinationCityOptions] = React.useState<
      SelectContentProps[]
    >([]);

    // Carrega cidades conforme a UF selecionada em origem ou destino.
    const originCityOptionsMutation = useMutation({
      mutationFn: fetchCities,
      onSuccess: data => setOriginCityOptions(data.singleSelectOptions),
    });

    const destinationCityOptionsMutation = useMutation({
      mutationFn: fetchCities,
      onSuccess: data => setDestinationCityOptions(data.singleSelectOptions),
    });

    // Formulário e campos observados para habilitar/desabilitar cidades.
    const form = useForm<FormSchema>({
      resolver: zodResolver(formSchema),
    });

    const selectedOriginUf = form.watch('originUf');
    const selectedDestinationUf = form.watch('destinationUf');

    const loadOriginCities = React.useCallback(
      (uf: string) => {
        originCityOptionsMutation.mutate(uf);
      },
      [originCityOptionsMutation]
    );

    const loadDestinationCities = React.useCallback(
      (uf: string) => {
        destinationCityOptionsMutation.mutate(uf);
      },
      [destinationCityOptionsMutation]
    );

    // Ao trocar a UF manualmente, a cidade selecionada deixa de ser válida.
    const onOriginUfChange = React.useCallback(
      (uf: string) => {
        form.resetField('originIbge');
        loadOriginCities(uf);
      },
      [form, loadOriginCities]
    );

    // Ao limpar a UF, remove também as cidades disponíveis.
    const onOriginUfClear = React.useCallback(() => {
      form.resetField('originIbge');
      setOriginCityOptions([]);
    }, [form]);

    const onDestinationUfChange = React.useCallback(
      (uf: string) => {
        form.resetField('destinationIbge');
        loadDestinationCities(uf);
      },
      [form, loadDestinationCities]
    );

    // Ao limpar a UF, remove também as cidades disponíveis.
    const onDestinationUfClear = React.useCallback(() => {
      form.resetField('destinationIbge');
      setDestinationCityOptions([]);
    }, [form]);

    // Restaura os filtros persistidos no store quando a tela é aberta.
    const hydrateForm = React.useCallback(() => {
      const {
        supplierId,
        companyId,
        carrierId,
        invoiceNumber,
        emissionDateFrom,
        emissionDateTo,
        deliveryDate,
        sameCarrierCnpj,
        originUf,
        originIbge,
        destinationUf,
        destinationIbge,
      } = filters;

      form.setValue('supplierId', supplierId);
      form.setValue('companyId', companyId);
      form.setValue('carrierId', carrierId);
      form.setValue('invoiceNumber', invoiceNumber ?? '');
      form.setValue('emissionDate', {
        ...(emissionDateFrom && {
          from: new Date(`${emissionDateFrom}T00:00:00`),
        }),
        ...(emissionDateTo && {
          to: new Date(`${emissionDateTo}T23:59:59`),
        }),
      });
      deliveryDate && form.setValue('deliveryDate', new Date(deliveryDate));
      form.setValue('sameCarrierCnpj', sameCarrierCnpj);
      form.setValue('originUf', originUf);
      originUf && loadOriginCities(originUf);
      form.setValue('originIbge', originIbge);
      form.setValue('destinationUf', destinationUf);
      destinationUf && loadDestinationCities(destinationUf);
      form.setValue('destinationIbge', destinationIbge);
      setFormHydrated(true);
    }, [filters, form, loadDestinationCities, loadOriginCities]);

    // Limpa formulário, opções locais e filtros persistidos.
    const resetForm = React.useCallback(() => {
      form.reset(EMPTY_FORM_VALUES, { keepValues: false });
      setOriginCityOptions([]);
      setDestinationCityOptions([]);
      onResetFilters();
    }, [form, onResetFilters]);

    // Converte o formulário para os filtros usados pela listagem, preservando o indicador selecionado.
    const onFormSubmit = React.useCallback(
      (formData: FormSchema) => {
        onSubmitFilters({
          ...createFilters(formData),
          filterIndicators: filters.filterIndicators,
        });
      },
      [filters.filterIndicators, onSubmitFilters]
    );

    React.useEffect(() => {
      if (!formHydrated) hydrateForm();
    }, [formHydrated, hydrateForm]);

    // Permite que o botão "Limpar filtros" do cabeçalho acione o reset deste formulário.
    React.useImperativeHandle(
      ref,
      () => ({ resetFilters: resetForm }),
      [resetForm]
    );

    return (
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onFormSubmit)} className="w-full">
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-5">
            <FormField
              control={form.control}
              name="supplierId"
              render={({ field }) => (
                <MultiSelectGs
                  form={form}
                  fieldName={field.name}
                  fieldValue={field.value}
                  label="Fornecedor"
                  maxCount={1}
                  options={supplierOptions.multiSelectOptions}
                  classNames={{
                    selectedBadge:
                      'md:max-w-[200px] lg:max-w-[180px] xl:max-w-[150px]',
                  }}
                />
              )}
            />
            <FormField
              control={form.control}
              name="companyId"
              render={({ field }) => (
                <MultiSelectGs
                  form={form}
                  fieldName={field.name}
                  fieldValue={field.value}
                  label="Centro"
                  maxCount={1}
                  options={companyOptions.multiSelectOptions}
                  classNames={{
                    selectedBadge:
                      'md:max-w-[200px] lg:max-w-[180px] xl:max-w-[150px]',
                  }}
                />
              )}
            />
            <FormField
              control={form.control}
              name="invoiceNumber"
              render={({ field }) => (
                <InputGs
                  {...field}
                  type="text"
                  label="NF"
                  placeholder="NFs separadas por vírgula"
                />
              )}
            />
            <FormField
              control={form.control}
              name="emissionDate"
              render={({ field }) => (
                <DateRangePickerGs
                  selected={{
                    from: field.value?.from,
                    to: field.value?.to,
                  }}
                  onSelect={field.onChange}
                  label="Emissão"
                />
              )}
            />
            <FormField
              control={form.control}
              name="deliveryDate"
              render={({ field }) => (
                <DatePickerGs
                  {...field}
                  label="Entrega"
                  fieldValue={field.value ? new Date(field.value) : undefined}
                  fieldOnChange={field.onChange}
                  maxDate={addYears(new Date(), 100)}
                />
              )}
            />
            <FormField
              control={form.control}
              name="carrierId"
              render={({ field }) => (
                <MultiSelectGs
                  form={form}
                  fieldName={field.name}
                  fieldValue={field.value}
                  label={
                    <LabelAndCheckbox
                      form={form}
                      mainLabel="Transportadora"
                      checkBoxLabel="CNPJ raiz"
                      checkBoxName="sameCarrierCnpj"
                    />
                  }
                  maxCount={1}
                  options={carrierOptions.multiSelectOptions}
                  classNames={{
                    selectedBadge:
                      'md:max-w-[200px] lg:max-w-[180px] xl:max-w-[150px]',
                  }}
                />
              )}
            />
            <FormField
              control={form.control}
              name="originUf"
              render={({ field }) => (
                <ComboboxGs
                  label="UF de origem"
                  form={form}
                  fieldName={field.name}
                  fieldValue={field.value}
                  content={ufOptions.singleSelectOptions}
                  onChange={onOriginUfChange}
                  onClear={onOriginUfClear}
                  clearable
                />
              )}
            />
            <FormField
              control={form.control}
              name="originIbge"
              render={({ field }) => (
                <ComboboxGs
                  label="Origem"
                  form={form}
                  fieldName={field.name}
                  fieldValue={field.value}
                  content={originCityOptions}
                  loading={originCityOptionsMutation.isLoading}
                  disabled={!selectedOriginUf}
                  clearable
                />
              )}
            />
            <FormField
              control={form.control}
              name="destinationUf"
              render={({ field }) => (
                <ComboboxGs
                  label="UF de destino"
                  form={form}
                  fieldName={field.name}
                  fieldValue={field.value}
                  content={ufOptions.singleSelectOptions}
                  onChange={onDestinationUfChange}
                  onClear={onDestinationUfClear}
                  clearable
                />
              )}
            />
            <FormField
              control={form.control}
              name="destinationIbge"
              render={({ field }) => (
                <ComboboxGs
                  label="Destino"
                  form={form}
                  fieldName={field.name}
                  fieldValue={field.value}
                  content={destinationCityOptions}
                  loading={destinationCityOptionsMutation.isLoading}
                  disabled={!selectedDestinationUf}
                  clearable
                />
              )}
            />
          </div>
          <div className="mt-6 flex items-center gap-4">
            <Button.Root
              type="button"
              size="sm"
              variant="secondary"
              onClick={resetForm}
            >
              Limpar filtros
            </Button.Root>
            <Button.Root type="submit" size="sm" variant="primary">
              Filtrar
            </Button.Root>
          </div>
        </form>
      </Form>
    );
  }
);

DeliveryVoucherFilters.displayName = 'DeliveryVoucherFilters';
